/**
 * Service layer — Slack Discussion Threading
 *
 * All Slack API calls route through /api/slack (Vercel serverless).
 * The Slack bot token lives server-side only — never in frontend code.
 * Client passes projectId; the server resolves the channel ID.
 *
 * Per-user OAuth: if a Clerk JWT is passed, the server uses the user's own
 * Slack token (xoxp-...) so messages post without the APP badge. Falls back
 * to the bot token for users who haven't connected yet.
 */

/**
 * Base POST to /api/slack. Optionally includes a Clerk JWT so the server
 * can look up and use the caller's personal Slack token.
 */
function slackApi(action, params, clerkToken) {
  const headers = { 'Content-Type': 'application/json' }
  if (clerkToken) headers['Authorization'] = `Bearer ${clerkToken}`
  return fetch('/api/slack', {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, ...params }),
  }).then(r => r.json())
}

/**
 * Start a new Slack thread for any entity (milestone, sheet, or BIM system).
 * Posts an opening message to the project channel and saves the thread timestamp
 * to the entity's Airtable record.
 * @param {string} [clerkToken] — if provided, posts as the real Slack user
 * @returns {Promise<{ ts?: string, permalink?: string, error?: string }>}
 */
export function startDiscussion({ entityId, entityType, entityName, projectId, projectName, startedBy, deepLink }, clerkToken) {
  return slackApi('startDiscussion', { entityId, entityType, entityName, projectId, projectName, startedBy, deepLink }, clerkToken)
}

/**
 * Begin a 1:1 Slack DM with a person (by their Member record id). The server resolves their Slack
 * user id and, if the caller has connected Slack, posts the seed `text` into the DM as the caller.
 * @param {string} [clerkToken] — if provided, posts as the real Slack user
 * @returns {Promise<{ permalink?: string, deepLink?: string, needsConnect?: boolean, error?: string }>}
 */
export function startDirectMessage({ submitterMemberId, text }, clerkToken) {
  return slackApi('startDirectMessage', { submitterMemberId, text }, clerkToken)
}

/**
 * Save a person's Slack id (raw U…/W… id or an @handle/name) to their Contact so future DMs resolve.
 * @returns {Promise<{ slackUserId?: string, error?: string }>}
 */
export function setMemberSlackId({ submitterMemberId, input }, clerkToken) {
  return slackApi('setMemberSlackId', { submitterMemberId, input }, clerkToken)
}

/**
 * Fetch all messages in an existing Slack thread.
 * @returns {Promise<{ messages?: object[], permalink?: string, error?: string }>}
 */
export function getThread({ projectId, threadTs, entityType }) {
  return slackApi('getThread', { projectId, threadTs, entityType })
}

/**
 * Post a reply to an existing Slack thread.
 * @param {string} [clerkToken] — if provided, posts as the real Slack user
 * @returns {Promise<{ ts?: string, error?: string }>}
 */
export function replyToThread({ projectId, threadTs, message, authorName, entityType }, clerkToken) {
  return slackApi('replyToThread', { projectId, threadTs, message, authorName, entityType }, clerkToken)
}

/**
 * Post a pasted/copied image into the discussion. Starts the thread itself (same as
 * startDiscussion) if `threadTs` is empty/undefined — the server reports the resulting
 * ts back either way.
 * @param {string} imageBase64 — raw base64 (no `data:` prefix)
 * @param {string} [clerkToken] — if provided, posts as the real Slack user
 * @returns {Promise<{ ts?: string, warning?: string, error?: string }>}
 */
export function uploadImage({ entityId, entityType, entityName, projectId, projectName, threadTs, imageBase64, filename, comment }, clerkToken) {
  return slackApi('uploadImage', { entityId, entityType, entityName, projectId, projectName, threadTs, imageBase64, filename, comment }, clerkToken)
}

/**
 * Fetch workspace members for @mention autocomplete.
 * Requires users:read scope on the bot token.
 * @returns {Promise<{ members?: Array<{id, name, displayName, realName}>, error?: string }>}
 */
export function getMembers() {
  return slackApi('getMembers', {})
}

/**
 * Check whether the calling user has connected their personal Slack account.
 * @param {string} clerkToken — JWT from window.__clerkGetToken()
 * @returns {Promise<{ connected: boolean, slackUserId?: string }>}
 */
export function checkSlackConnection(clerkToken) {
  return slackApi('checkConnection', {}, clerkToken)
}

/**
 * Delete a message the current user posted.
 * Uses the caller's own xoxp- token — only works for your own messages.
 * @param {string} clerkToken — JWT from window.__clerkGetToken()
 * @returns {Promise<{ ok?: boolean, error?: string }>}
 */
export function deleteMessage({ projectId, messageTs, entityType }, clerkToken) {
  return slackApi('deleteMessage', { projectId, messageTs, entityType }, clerkToken)
}

/**
 * Add an emoji reaction to a specific message.
 * Uses the caller's own Slack token if connected, falls back to bot token.
 * @param {string} name — emoji name without colons, e.g. 'thumbsup'
 * @param {string} [clerkToken]
 * @returns {Promise<{ ok?: boolean, error?: string }>}
 */
export function addReaction({ projectId, messageTs, name, entityType }, clerkToken) {
  return slackApi('addReaction', { projectId, messageTs, name, entityType }, clerkToken)
}

/**
 * Remove an emoji reaction from a specific message.
 * @param {string} name — emoji name without colons, e.g. 'thumbsup'
 * @param {string} [clerkToken]
 * @returns {Promise<{ ok?: boolean, error?: string }>}
 */
export function removeReaction({ projectId, messageTs, name, entityType }, clerkToken) {
  return slackApi('removeReaction', { projectId, messageTs, name, entityType }, clerkToken)
}

/**
 * Get the Slack OAuth URL that starts the per-user connection flow.
 * Makes a GET to /api/slack-connect with the Clerk JWT for identity.
 * @param {string} clerkToken — JWT from window.__clerkGetToken()
 * @returns {Promise<{ url?: string, error?: string }>}
 */
export async function getSlackConnectUrl(clerkToken) {
  const res = await fetch('/api/slack-connect', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${clerkToken}`,
      'Content-Type': 'application/json',
    },
  })
  return res.json()
}

/**
 * Open a milestone's Slack discussion in the person's own Slack (Matt, 2026-11-02, from the weekly plan:
 * "a discuss in Slack button that brings me into context in my own slack window"). Reuses the
 * milestone's thread when it has one, else starts it in the project channel. The tab is opened first,
 * synchronously, so the browser does not block it as a pop-up.
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function openMilestoneDiscussion({ msId, msName, projectId, projectName, startedBy, deepLink }) {
  const tab = window.open('about:blank', '_blank')
  try {
    const { getMilestoneRecord } = await import('./milestones.js')
    const rec = await getMilestoneRecord(msId)
    const f = (rec && rec.fields) || {}
    const pid = projectId || (f['Project'] || [])[0]
    const ts = f['Slack Thread Timestamp']
    const token = window.__clerkGetToken ? await window.__clerkGetToken() : null
    let res
    if (ts) res = await getThread({ projectId: pid, threadTs: ts, entityType: 'milestone' })
    if (!res || !res.permalink) res = await startDiscussion({ entityId: msId, entityType: 'milestone', entityName: msName || f['Full Deliverable Name'] || 'Milestone', projectId: pid, projectName, startedBy, deepLink }, token)
    if (res && res.permalink) { if (tab) tab.location.href = res.permalink; else window.open(res.permalink, '_blank'); return { ok: true } }
    if (tab) tab.close()
    return { ok: false, error: (res && res.error) || 'Slack did not answer.' }
  } catch (e) {
    if (tab) tab.close()
    return { ok: false, error: String((e && e.message) || e) }
  }
}
